import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { GetBriefMonitorDataRes } from '@/api/type-response'
import { Divider, Loading, Trans } from '@/components'
import { EmptyView } from '@/components/EmptyView'
import { useBriefInstanceMonitorData } from '@/hooks/useBriefInstanceMonitorData'
import { Instance } from '@/models/instance'
import { cn } from '@/utils/style'

interface InstanceMonitorProps {
  instance: Instance
  navigationPath?: string
  backPath?: string
}

interface MonitorItem {
  key: string
  name: string
  value: string | React.ReactElement
}

const convertMbToGb = (val: any) => (Number(val) || 0) / 1024

export function InstanceMonitor({ instance, navigationPath, backPath }: InstanceMonitorProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()

  // Use hook to fetch monitor data with auto-polling
  const { info, loading, grantPending, isTeamContext } = useBriefInstanceMonitorData({
    instance,
    pollingInterval: 30000,
  })

  // Determine navigation paths based on context
  const finalNavigationPath =
    navigationPath || (isTeamContext ? '/team/instances/monitor' : '/instances/monitor')
  const finalBackPath = backPath || (isTeamContext ? location.pathname : '/instances')

  // Show loading when either granting token or fetching data
  const showLoading = grantPending || (loading && !info)

  const infoMap = {
    cpuUsage: [
      t('instance.detail.monitor.cpuUsage'),
      [
        {
          value: (res: GetBriefMonitorDataRes) =>
            `${(res.usageState?.cpuUtilPercent || 0).toFixed(2)}`,
          unit: '%',
          type: 'primary',
        },
      ],
    ],
    memoryUsage: [
      t('instance.detail.monitor.memoryUsage'),
      [
        {
          value: (res: GetBriefMonitorDataRes) =>
            `${convertMbToGb(res.usageState?.ramUsageMB || 0).toFixed(2)}`,
          unit: '',
          type: 'primary',
        },
        {
          value: (res: GetBriefMonitorDataRes) =>
            `/${convertMbToGb(res.hardware?.machine?.ramMB || 0).toFixed(2)}`,
          unit: 'GB',
          type: 'tertiary',
        },
      ],
    ],
    diskUsage: [
      t('instance.detail.monitor.diskUsage'),
      [
        {
          value: (res: GetBriefMonitorDataRes) =>
            `${(res.usageState?.diskUsageGB || 0).toFixed(2)}`,
          unit: '',
          type: 'primary',
        },
        {
          value: (res: GetBriefMonitorDataRes) => `/${res.hardware?.machine?.diskGB || 0}`,
          unit: 'GB',
          type: 'tertiary',
        },
      ],
    ],
    // bandwidthUsage: [
    //   'Bandwidth Usage',
    //   [
    //     {
    //       value: (res: GetBriefMonitorDataRes) => `${res.hardware.machine.internetDownloadMbs}`,
    //       unit: '',
    //       type: 'primary',
    //     },
    //     {
    //       value: '',
    //       unit: '',
    //       type: 'primary',
    //     },
    //   ],
    // ],
  }

  const hwInfos = useMemo<MonitorItem[]>(() => {
    if (!info) {
      return []
    }
    const infos = [] as MonitorItem[]

    Object.entries(infoMap).forEach(([key, i]) => {
      if (Array.isArray(i)) {
        const name = i[0] as string
        const values = i[1]
        if (Array.isArray(values)) {
          infos.push({
            key,
            name,
            value: (
              <div className="flex">
                {...values.map((v, index) => [
                  <div
                    key={`v${index}`}
                    className={cn(
                      't-sb',
                      v.type === 'primary' ? ' text-primary' : ' text-tertiary',
                    )}
                  >
                    {typeof v.value === 'string' ? v.value : v.value(info)}
                  </div>,
                  <div
                    key={`u${index}`}
                    className={cn(
                      't-sb',
                      v.type === 'primary' ? ' text-primary' : ' text-tertiary',
                    )}
                  >
                    {v.unit}
                  </div>,
                ])}
              </div>
            ),
          })
        }
      }
    })

    return infos
  }, [info])

  const gpuInfos = useMemo<MonitorItem[]>(() => {
    if (!info || !info.usageState?.gpuState) {
      return []
    }
    return info.usageState.gpuState
      .map((gpu, index) => [
        {
          key: `gpuCore${index + 1}`,
          name: `GPU Core-${index + 1 < 10 ? '0' : ''}${index + 1}`,
          value: (
            <div className="flex">
              <div className="t-sb text-primary">{gpu.gpuUtilPercent}</div>
              <div className="t-sb text-primary">%</div>
            </div>
          ),
        },
        {
          key: `gpuMemory${index + 1}`,
          name: `GPU Memory-${index + 1 < 10 ? '0' : ''}${index + 1}`,
          value: (
            <div className="flex">
              <div className="t-sb text-primary">{convertMbToGb(gpu.gpuRamUsageMB).toFixed(2)}</div>
              <div className="t-sb text-tertiary">
                /
                {(
                  convertMbToGb(info.hardware?.gpu?.gpuRamMB || 0) /
                  (info.usageState?.gpuState?.length || 1)
                ).toFixed(2)}{' '}
                GB
              </div>
            </div>
          ),
        },
      ])
      .flat()
  }, [info])

  return (
    <div className="bg-panel-gray-100 rounded-lg flex flex-wrap py-4 t-sm text-tertiary gap-y-4">
      {showLoading ? (
        <div className="w-full flex items-center justify-center p-8">
          <Loading />
        </div>
      ) : !info ? (
        <>
          <EmptyView
            title={
              <span className="t-sm text-secondary">
                <Trans i18nKey="common.emptyViewNoDataMsg">No Data</Trans>
              </span>
            }
            className="py-0"
          />
          <div className="w-full px-4 flex flex-col items-start justify-end">
            <Divider className="mb-3" />
            <div
              className="t-sb cursor-pointer text-btn-secondary"
              onClick={() =>
                navigate(finalNavigationPath, { state: { instance, from: finalBackPath } })
              }
            >
              <Trans i18nKey="instance.detail.monitor.viewDetailBtn">View Details</Trans>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="w-full grid grid-cols-4 gap-x-4 px-4">
            {hwInfos.map((hw: MonitorItem) => (
              <div key={hw.key} className="w-1/4">
                <div className="text-secondary t-xs">{hw.name}</div>
                <div className="t-sb text-primary">{hw.value}</div>
              </div>
            ))}
          </div>
          {gpuInfos.length > 0 && (
            <>
              <div className="w-full px-4 flex items-center gap-x-4">
                <div className="t-xs text-nowrap">
                  <Trans i18nKey="instance.detail.monitor.gpuInfoTitle">GPU Information</Trans>
                </div>
                <Divider className="flex-grow mb-0" />
              </div>
              <div className="w-full grid grid-cols-4 gap-4 px-4">
                {gpuInfos.map((gpu: MonitorItem) => (
                  <div key={gpu.key} className="w-full">
                    <div className="text-secondary t-xs font-medium">{gpu.name}</div>
                    <div className="t-sb text-primary">{gpu.value}</div>
                  </div>
                ))}
              </div>
            </>
          )}
          <div className="w-full px-4 flex flex-col items-start justify-end">
            <Divider className="mb-3" />
            <div
              className="t-sb font-medium cursor-pointer text-btn-secondary"
              onClick={() =>
                navigate(finalNavigationPath, { state: { instance, from: finalBackPath } })
              }
            >
              <Trans i18nKey="instance.detail.monitor.viewDetailBtn">View Details</Trans>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
